import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Checkbox
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useState } from 'react';
import calculatePrice from '../../../../utils/calculators/calculatePrice';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';
import { useApi } from '../../../../context/ApiProvider';
import { CustomAlertDialogInterface } from '../../../../shared/type/CustomAlertDialog.type';
import { useAlertContext } from '../../../../context/AlertProvider';
import { ConfirmCancelingPopup } from './ConfirmCancelingPopup';
import { ConfirmCancelingPaymentPopup } from './ConfirmCancelingPaymentPopup';
import { useOrderDetailsData } from '../../../../hooks/useOrderDetailsData';
import { OrderItemInterface } from '../../../../shared/type/parentOrderHistory.type';
import useAuth from '../../../../hooks/useAuth';


interface Props {
  isOpen: boolean;
  onClose: () => void;
  afterAction?: () => void;
  orderId: number;
  generalOrderData: OrderItemInterface | undefined;
}

const ParentOrderHistoryDetails = ({
  isOpen,
  onClose,
  orderId,
  generalOrderData,
  afterAction
}: Props) => {
  const { apiTenantAdminBoughtStakesController, apiTenantAdminPaymentController } = useApi();
  const { order, isLoading } = useOrderDetailsData(orderId);
  const { setAlertProperties, setShow: setShowAlert } = useAlertContext();
  const [selectedStakeIds, setSelectedStakeIds] = useState<number[]>([]);
  const [showConfirmPopup, setShowCancelPopup] = useState<boolean>(false);
  const [showCancelPaymentPopup, setShowCancelPaymentPopup] = useState<boolean>(false);
  const { auth } = useAuth();
  const isAdminAccount = auth.loggedUserDto.tenantAdmin || auth.loggedUserDto.superAdmin;
  const formattedOrderDate = order?.creationDate
    ? moment(order.creationDate).format('DD.MM.YYYY HH:mm')
    : '';
  const allStakeIds =
    order?.boughtStakeDays?.flatMap((day) => day.boughtStakes.map((stake) => stake.id)) || [];

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedStakeIds(allStakeIds);
    } else {
      setSelectedStakeIds([]);
    }
  };

  const areAllSelected = allStakeIds.length > 0 && selectedStakeIds.length === allStakeIds.length;

  const toggleSingleStake = (stakeId: number, checked: boolean) => {
    setSelectedStakeIds((prev) =>
      checked ? [...prev, stakeId] : prev.filter((id) => id !== stakeId)
    );
  };

  const getLength = (order: any): number => {
    return order?.boughtStakeDays?.length;
  };
  const isCashPayment = (order: any): boolean => {
    return order?.boughtStakeDays?.length === 0 && order?.paymentType === 'CASH';
  };

  const onCloseConfirmPopup = () => {
    setShowCancelPopup(false);
  };
  const onCloseConfirmPaymentPopup = () => {
    setShowCancelPaymentPopup(false);
  };

  const getAlertBody = (string: string): CustomAlertDialogInterface => ({
    status: 'error',
    title: 'Błąd',
    description: `Nie udało się anulować ${string}`,
    timeout: 9000
  });

  const handleCancelSelectedStakes = async () => {
    try {
      const response = await apiTenantAdminBoughtStakesController('cancel-bought-stakes').post('', {
        boughtStakeIds: selectedStakeIds,
        consumerId: order?.consumerId
      });
      if (response.status === 200) {
        setAlertProperties({
          status: 'success',
          title: 'Sukces',
          description: `Pomyślnie anulowano stawki a środki zostały przekazane na saldo konsumenta`,
          timeout: 9000
        });
        setShowAlert(true);
        afterAction?.();
        onClose();
      }
    } catch (error: any) {
      setAlertProperties(getAlertBody(`stawki: ${error.response.data.errorMessage}`));
      setShowAlert(true);
    }
  };
  const handleCancelSelectedStakesWithCashWithdrawal = async () => {
    try {
      const response = await apiTenantAdminBoughtStakesController(
        'cancel-bought-stakes-with-cash-withdrawal'
      ).post('', {
        boughtStakeIds: selectedStakeIds,
        consumerId: order?.consumerId
      });

      if (response.status === 200) {
        setAlertProperties({
          status: 'success',
          title: 'Sukces',
          description: `Pomyślnie anulowano stawki bez wpłacania środków na saldo`,
          timeout: 9000
        });
        setShowAlert(true);
        afterAction?.();
        onClose();
      }
    } catch (error: any) {
      setAlertProperties(getAlertBody(`stawki: ${error.response.data.errorMessage}`));
      setShowAlert(true);
    }
  };
  const handleCancelSelectedPayment = async () => {
    try {
      const response = await apiTenantAdminPaymentController('cancel-payment').post('', {
        orderId: order?.id
      });

      if (response.status === 200) {
        setAlertProperties({
          status: 'success',
          title: 'Sukces',
          description: `Pomyślnie anulowano płatność`,
          timeout: 9000
        });
        setShowAlert(true);
        afterAction?.();
        onClose();
      }
    } catch (error: any) {
      setAlertProperties(getAlertBody(`stawki: ${error.response.data.errorMessage}`));
      setShowAlert(true);
    }
  };

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} size="5xl" isCentered>
      <ModalOverlay
        sx={{
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
      />
      <ModalContent>
        <ModalHeader className="flex flex-col gap-spacing-xl">
          {isLoading ? (
            <h2 className="font-semibold text-lg text-grayLight-900">Trwa pobieranie danych...</h2>
          ) : (
            <h2 className="font-semibold text-lg text-grayLight-900">
              Zamówienie {formattedOrderDate}
            </h2>
          )}
          <div className="border-b border-grayLight-200" />
        </ModalHeader>
        <ModalCloseButton />
        {!isLoading && !!order && (
          <ModalBody className="grid gap-spacing-3xl">
            <div className="w-96">
              <table className="border border-gray-300 w-full">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="p-2 border-r border-gray-300">
                      <p className="text-xs font-medium text-grayLight-600">Saldo przed</p>
                    </th>
                    <th className="p-2 border-r border-gray-300">
                      <p className="text-xs font-medium text-grayLight-600">Wpłata</p>
                    </th>
                    <th className="p-2">
                      <p className="text-xs font-medium text-grayLight-600">Saldo po</p>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-300">
                    <td className="p-2 border-r border-gray-300 pt-spacing-xl">
                      <p className="text-xs font-medium text-grayLight-600">
                        {generalOrderData?.saldoBefore}
                      </p>
                    </td>
                    <td className="p-2 border-r border-gray-300">
                      <p className="text-xs font-medium text-grayLight-600">
                        {generalOrderData?.paymentValue}
                      </p>
                    </td>
                    <td className="p-2">
                      <p className="text-xs font-medium text-grayLight-600">
                        {generalOrderData?.saldoAfter}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="w-full flex justify-end">
              {isAdminAccount && isCashPayment(order) && (
                <CustomChakraButton
                  hierarchy="primary"
                  size="sm"
                  iconPosition="onlyText"
                  buttonProps={{
                    type: 'button',
                    onClick: () => setShowCancelPaymentPopup(true)
                  }}>
                  Wycofaj płatność
                </CustomChakraButton>
              )}
              {isAdminAccount && getLength(order) > 0 && (
                <CustomChakraButton
                  hierarchy="primary"
                  size="sm"
                  iconPosition="onlyText"
                  buttonProps={{
                    type: 'button',
                    onClick: () => setShowCancelPopup(true),
                    disabled: selectedStakeIds.length === 0
                  }}>
                  Wycofaj stawki
                </CustomChakraButton>
              )}
            </div>
            <div>
              {getLength(order) > 0 && (
                <table className="border border-gray-300 w-full">
                  <thead className="bg-blue-25">
                    <tr className="border-b border-gray-300">
                      <th className="p-2 border-r border-gray-300">
                        <p className="text-xs font-medium text-grayLight-600">Lp</p>
                      </th>
                      <th className="p-2 border-r border-gray-300">
                        <p className="text-xs font-medium text-grayLight-600">Dzień</p>
                      </th>
                      <th className="p-2 border-r border-gray-300">
                        <p className="text-xs font-medium text-grayLight-600">Dla</p>
                      </th>
                      <th className="p-2 border-r border-gray-300">
                        <p className="text-xs font-medium text-grayLight-600">Nazwa</p>
                      </th>
                      <th className="p-2 border-r border-gray-300">
                        <p className="text-xs font-medium text-grayLight-600">Wartość stawki</p>
                      </th>
                      {isAdminAccount && <th className="p-2">
                        <Checkbox
                          colorScheme="customOrange"
                          isChecked={areAllSelected}
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                        />
                      </th>
                      }
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-300">
                      <td className="p-2 border-r border-gray-300 pt-spacing-s" />
                      <td className="p-2 border-r border-gray-300 pt-spacing-xl" />
                      <td className="p-2 border-r border-gray-300 pt-spacing-xl">
                        <p className="text-xs font-medium text-grayLight-600">Razem</p>
                      </td>
                      <td className="p-2 border-r border-gray-300 pt-spacing-xl">
                        <p className="text-xs font-medium text-grayLight-600">
                          {generalOrderData?.orderValueString}
                        </p>
                      </td>
                    </tr>
                    {order?.boughtStakeDays.map((day, index) => (
                      <tr key={index} className="border-b border-gray-300">
                        <td className="p-2 border-r border-gray-300 pt-spacing-s pr-spacing-md pb-spacing-xl pl-spacing-md">
                          <p className="text-xs font-medium text-grayLight-600">{index + 1}</p>
                        </td>
                        <td className="p-2 border-r border-gray-300 pt-spacing-xl pr-spacing-md pb-spacing-xl pl-spacing-md">
                          <p className="text-xs font-medium text-grayLight-600">{day.when}</p>
                        </td>
                        <td className="p-2 border-r border-gray-300 pt-spacing-xl pr-spacing-md pb-spacing-xl pl-spacing-md">
                          <p className="text-xs font-medium text-grayLight-600">
                            {generalOrderData?.consumerDto.firstName}{' '}
                            {generalOrderData?.consumerDto.lastName}
                          </p>
                        </td>
                        <td className="p-2 border-r border-gray-300 pt-spacing-xl pr-spacing-md pb-spacing-xl pl-spacing-md">
                          {day.boughtStakes.map((stake, stakeIndex) => (
                            <p key={stakeIndex} className="text-xs font-medium text-grayLight-600">
                              {stake.name}
                            </p>
                          ))}
                        </td>
                        <td className="p-2 border-r border-gray-300 pt-spacing-xl pr-spacing-md pb-spacing-xl pl-spacing-md">
                          {day.boughtStakes.map((stake, stakeIndex) => (
                            <p key={stakeIndex} className="text-xs font-medium text-grayLight-600">
                              {calculatePrice(stake.price, 'zł')}
                            </p>
                          ))}
                        </td>{' '}
                        {isAdminAccount && <td className="p-2 border-r border-gray-300 pt-spacing-xl pr-spacing-md pb-spacing-xl pl-spacing-md text-center">
                          {day.boughtStakes.map((stake, stakeIndex) => (
                            <div key={stakeIndex}>
                              <Checkbox
                                colorScheme="customOrange"
                                isChecked={selectedStakeIds.includes(stake.id)}
                                onChange={(e) => toggleSingleStake(stake.id, e.target.checked)}
                              />
                            </div>
                          ))}
                        </td>
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </ModalBody>
        )}
      </ModalContent>
      <ConfirmCancelingPopup
        isOpen={showConfirmPopup}
        onClose={onCloseConfirmPopup}
        onConfirm={() => handleCancelSelectedStakes()}
        onConfirmWithCashWithdrawal={() => handleCancelSelectedStakesWithCashWithdrawal()}
      />
      <ConfirmCancelingPaymentPopup
        isOpen={showCancelPaymentPopup}
        onClose={onCloseConfirmPaymentPopup}
        onConfirm={() => handleCancelSelectedPayment()}
      />
    </Modal>
  );
};

export default ParentOrderHistoryDetails;
